import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { Constants } from '../../constants/constants';
import { DeviceModel } from '../../models/device.model';
import { AppearanceService } from '../appearance/appearance.service';
import { UserModel } from '../../models/user.model';
import { ClientModel } from 'src/app/models/client.model';
import { TabModel } from 'src/app/models/tab.model';
import { FilterModel } from 'src/app/models/filter.model';
import { AssetFieldModel } from 'src/app/models/asset_field.model';
import { ObservationModel } from 'src/app/models/observation.model';
import { DtRowModel } from 'src/app/models/datatable/dt_row.model';
import { StatisticFilterModel } from 'src/app/models/statistic-filter.model';


@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  private settingsKey: string = Constants.kStorage;

  private defaults = {
    "storage": 'cat'
  };

  settings: any;


  constructor(private storage: Storage, private appearanceService: AppearanceService) 
  { 
    storage.create().then(() => {
      // Storage is ready to use
      // Note: ready() is only available in 1.1.7 or greater!
      this.load();
    });
  }

  saveLastAssetFields(fields)
  {
    this.setValue(Constants.kLastAssF, fields);
  }

  getLastAssetFields(): Array<AssetFieldModel> {
    try {
        return this.getValue(Constants.kLastAssF);
    } catch(e) {
        return [];
    }
  }


  saveLastObservation(obs)
  {
    this.setValue(Constants.kLastObs, obs);
  }

  getLastObservation(): ObservationModel {
    try {
        return this.getValue(Constants.kLastObs);
    } catch(e) {
        return null;
    }
  }

  saveUser(user)
  {
    this.setValue(Constants.kUser, user);
  }

  getUser(): UserModel {

    try {
      return this.getValue(Constants.kUser);
    } catch(e) {
        return null;
    }
  }


  passwordResetInProgress(toggle)
  {
    this.setValue(Constants.kPasswordReset, toggle);
  }

  isPasswordResetInProgress(): boolean {

    try {
      return this.getValue(Constants.kPasswordReset);
    } catch(e) {
        return false;
    }
  }


  saveClient(client)
  {
    this.setValue(Constants.kClient, client);
  }

  getClient(): ClientModel {

    try {
      return this.getValue(Constants.kClient);
    } catch(e) {
        return null;
    }
  }

  saveSelectedOrganisation(organisation)
  {
    this.setValue(Constants.kOrg, organisation);
  }

  getSelectedOrganisation(): FilterModel {

    try {
      return this.getValue(Constants.kOrg);
    } catch(e) {
        return null;
    }
  }

  saveAllClients(clients)
  {
    this.setValue(Constants.kClients, clients);
  }

  getAllClients(): Array<FilterModel> {

    try {
      return this.getValue(Constants.kClients);
    } catch(e) {
        return [];
    }
  }


  saveCurrentPageLength(columns, page)
  {
    this.setValue(Constants.kPgLnth + page, columns);
  }

  getCurrentPageLength(page): any {

    try {
      return this.getValue(Constants.kPgLnth + page);
    } catch(e) {
        return 10;
    }
  }


  saveCurrentPageCount(columns, page)
  {
    this.setValue(Constants.kPgCount + page, columns);
  }

  getCurrentPageCount(page): any {

    try {
      return this.getValue(Constants.kPgCount + page);
    } catch(e) {
        return 0;
    }
  }


  saveColumnVisibility(columns, page)
  {
    this.setValue(Constants.kColVis + page, columns);
  }

  getColumnVisibility(page): any {

    try {
      return this.getValue(Constants.kColVis + page);
    } catch(e) {
        return null;
    }
  }


  saveSelectedRowsForPage(rows, page)
  {
    this.setValue(Constants.kSelRow + page, rows);
  }

  getSelectedRowsForPage(page): Array<DtRowModel> {

    try {
      return this.getValue(Constants.kSelRow + page);
    } catch(e) {
        return [];
    }
  }



  saveTabs(section, tabs)
  {
    this.setValue("currentTabSection", section);
    this.setValue("tabs_" + section, tabs);
  }

  getTabs(section): Array<TabModel> {

    try {
      return this.getValue("tabs_" + section);
    } catch(e) {
        return [];
    }
  }

  saveActiveTab(tab)
  {
    this.setValue("activeTab", tab);
  }

  getActiveTab(): TabModel {

    try {
      return this.getValue("activeTab");
    } catch(e) {
        return null;
    }
  }

  getCurentTabSection(): string {

    try {
      return this.getValue("currentTabSection");
    } catch(e) {
        return null;
    }
  }


  saveDevice(device) {

    this.setValue(Constants.kCurDev, device);
  }

  getDevice(): DeviceModel {

    try {
        return this.getValue(Constants.kCurDev);
    } catch(e) {
        return null;
    }
  }


  saveDeviceUuid(uuid) {

    this.setValue(Constants.kCurDevUuid, uuid);
  }

  getDeviceUuid(): string {

    try {
        return this.getValue(Constants.kCurDevUuid);
    } catch(e) {
        return null;
    }
  }


  setLightThemeEnabled(toggle)
  {
    this.setValue(Constants.kAppTheme, toggle);
  }

  getLightThemeEnabled(): boolean {
    try {
        return this.getValue(Constants.kAppTheme);
    } catch(e) {
        return true;
    }
  }



  saveEngineerStatsFilter(options)
  {
    this.setValue(Constants.kStatsFilter, options);
  }

  getEngineerStatsFilter(): StatisticFilterModel {
    try {
        return this.getValue(Constants.kStatsFilter);
    } catch(e) {
        return null;
    }
  }


  saveApiEndpointMode(url) {

    this.setValue(Constants.kEnvMode, url);
  }

  getApiEndpointMode(): number {

    try {
        return this.getValue(Constants.kEnvMode);
    } catch(e) {
        return Constants.API_PROD;
    }
  }



  // Storage


  clearAll()
  {
    this.saveUser(null);
    this.saveLastObservation(null);
    this.saveActiveTab(null);
    this.saveTabs('asstypes', null);
    this.saveTabs('assets', null);
    this.saveTabs('devices', null);
    this.saveTabs('analytics', null);
    this.saveTabs('jobs', null);
    this.saveTabs('locations', null);
    this.saveTabs('reports', null);
    this.saveTabs('stock', null);
    this.saveTabs('stockinv', null);
    this.saveTabs('stockpreset', null);
    this.saveTabs('users', null);
    this.saveLastAssetFields(null);
    this.saveColumnVisibility(null, 'asstypes');
    this.saveColumnVisibility(null, 'assets');
    this.saveColumnVisibility(null, 'devices');
    this.saveColumnVisibility(null, 'analytics');
    this.saveColumnVisibility(null, 'jobs');
    this.saveColumnVisibility(null, 'locations');
    this.saveColumnVisibility(null, 'reports');
    this.saveColumnVisibility(null, 'stock');
    this.saveColumnVisibility(null, 'stockinv');
    this.saveColumnVisibility(null, 'stockpreset');
    this.saveColumnVisibility(null, 'users');
    this.saveSelectedRowsForPage(null, 'asstypes');
    this.saveSelectedRowsForPage(null, 'assets');
    this.saveSelectedRowsForPage(null, 'devices');
    this.saveSelectedRowsForPage(null, 'analytics');
    this.saveSelectedRowsForPage(null, 'jobs');
    this.saveSelectedRowsForPage(null, 'locations');
    this.saveSelectedRowsForPage(null, 'reports');
    this.saveSelectedRowsForPage(null, 'stock');
    this.saveSelectedRowsForPage(null, 'stockinv');
    this.saveSelectedRowsForPage(null, 'stockpreset');
    this.saveSelectedRowsForPage(null, 'users');
    this.saveClient(null);
  }


  load() {

    if (Constants.CLEAR_STORAGE)
    {
      this.clearAll();
      this.storage.clear();
    }

    this.storage.get(this.settingsKey).then((value) => {
        if (value) {
          this.settings = value;

          if (this.getUser() != null)
          {
           
          }
          else 
          {
            this.appearanceService.triggerEvent('user:loggedout', {
              time: new Date()
            });
          }
          
        } else {
            this.setDefaults(this.defaults).then((val) => {
                this.settings = val;
            })
        }

        this.appearanceService.triggerEvent('storage:loaded', {
          time: new Date()
        });
        
        this.appearanceService.setTheme(this.getLightThemeEnabled());
      });
  }


  setDefaults(value: any) {
    return this.storage.set(this.settingsKey, value);
  }


  setValue(key: string, value: any) {

    if (this.settings)
    {
      this.settings[key] = value;
      this.storage.set(this.settingsKey, this.settings);
    }
  }
  

  getValue(key: string) {
      return this.settings[key];
  }
}
